/**
 * Brandstyle24 – Size Selector v2.1
 * Tab-based EU/UK/US size conversion with gender selection
 *
 * v2.1 Fixes:
 * - EU-only variants (e.g. "EU 38" without UK/US) now parse correctly
 * - Original dropdown only hidden when pills successfully render
 * - Line Item Property correctly injected into cart form
 */

(function () {
  'use strict';

  /* =============================================
     CONFIGURATION
     ============================================= */

  const CONFIG = {
    // Regex patterns to parse variant names (ORDER MATTERS - most specific first)
    patterns: [
      // EU 42 2/3 UK 8.5 (Adidas/Salomon fractions + UK)
      { regex: /EU\s+(\d+(?:\s+\d\/\d)?)\s+UK\s+([\d.]+)/, euGroup: 1, secondSystem: 'uk', secondGroup: 2 },
      // EU 41 1/3 US 8 (HOKA fractions + US)
      { regex: /EU\s+(\d+\s+\d\/\d)\s+US\s+([\d.]+)/, euGroup: 1, secondSystem: 'us', secondGroup: 2 },
      // EU 42.5 UK 8 (standard decimal + UK)
      { regex: /EU\s+([\d.]+)\s+UK\s+([\d.]+)/, euGroup: 1, secondSystem: 'uk', secondGroup: 2 },
      // EU 42.5 US 9 (standard decimal + US)
      { regex: /EU\s+([\d.]+)\s+US\s+([\d.]+)/, euGroup: 1, secondSystem: 'us', secondGroup: 2 },
      // EU 42 2/3 (Adidas/Salomon fractions, EU only)
      { regex: /EU\s+(\d+\s+\d\/\d)\s*$/, euGroup: 1, secondSystem: null, secondGroup: null },
      // EU 42.5 or EU 42 (EU only, no UK/US)
      { regex: /EU\s+([\d.]+)\s*$/, euGroup: 1, secondSystem: null, secondGroup: null }
    ],
    // Clothing size patterns (no EU prefix)
    clothingSizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '2XL', '3XL', '4XL', '5XL'],
    // Database URL (Shopify asset)
    databaseUrl: null // Set dynamically from liquid
  };

  /* =============================================
     STATE
     ============================================= */

  let state = {
    tab: 'eu',
    gender: null,
    selectedEU: null,
    database: null,
    brandData: null,
    variants: [],
    parsedVariants: [],
    isShoe: true,
    isClothing: false,
    availableTabs: ['eu'],
    tabOrder: ['eu', 'uk', 'us'],
    translations: {}
  };

  /* =============================================
     INITIALIZATION
     ============================================= */

  function init() {
    const container = document.querySelector('[data-size-selector]');
    if (!container) return;

    // Read data attributes set by liquid
    const productVendor = container.dataset.vendor || '';
    const productVariantsJson = container.dataset.variants || '[]';
    const databaseUrl = container.dataset.databaseUrl || '';
    const translationsJson = container.dataset.translations || '{}';

    state.translations = safeJsonParse(translationsJson, {});
    state.variants = safeJsonParse(productVariantsJson, []);

    // Detect UK market for tab order
    if (typeof Shopify !== 'undefined' && Shopify.country === 'GB') {
      state.tabOrder = ['uk', 'eu', 'us'];
    }

    // Parse variant names
    state.parsedVariants = parseVariants(state.variants);

    // Detect product type
    var detection = detectProductType(state.parsedVariants);
    state.isShoe = detection.isShoe;
    state.isClothing = detection.isClothing;

    if (state.isClothing) {
      // Clothing: only EU tab, render directly, hide original dropdown
      state.availableTabs = ['eu'];
      state.tab = 'eu';
      render(container);
      addSizeSelectorActiveClass();
      return;
    }

    if (!state.isShoe) {
      // Unknown product type: activate fallback (CSS shows original dropdown)
      container.classList.add('size-selector-fallback');
      return;
    }

    // Load size database
    if (databaseUrl) {
      fetch(databaseUrl)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          state.database = data;
          state.brandData = findBrandData(productVendor, data);
          enrichVariantsFromDatabase(state.parsedVariants, state.brandData);
          state.availableTabs = detectAvailableTabs(state.parsedVariants);
          state.tab = state.tabOrder.find(function (t) { return state.availableTabs.indexOf(t) !== -1; }) || 'eu';
          render(container);
          // Only hide original dropdown if we have pills to show
          if (hasPillsToRender()) {
            addSizeSelectorActiveClass();
          } else {
            container.classList.add('size-selector-fallback');
          }
        })
        .catch(function () {
          // Fallback: use only data from variant names
          state.availableTabs = detectAvailableTabs(state.parsedVariants);
          state.tab = state.tabOrder.find(function (t) { return state.availableTabs.indexOf(t) !== -1; }) || 'eu';
          render(container);
          if (hasPillsToRender()) {
            addSizeSelectorActiveClass();
          } else {
            container.classList.add('size-selector-fallback');
          }
        });
    } else {
      state.availableTabs = detectAvailableTabs(state.parsedVariants);
      state.tab = state.tabOrder.find(function (t) { return state.availableTabs.indexOf(t) !== -1; }) || 'eu';
      render(container);
      if (hasPillsToRender()) {
        addSizeSelectorActiveClass();
      } else {
        container.classList.add('size-selector-fallback');
      }
    }
  }

  /* =============================================
     PARSING
     ============================================= */

  function safeJsonParse(str, fallback) {
    try { return JSON.parse(str); }
    catch (e) { return fallback; }
  }

  function parseVariants(variants) {
    return variants.map(function (v) {
      var parsed = {
        id: v.id,
        title: v.title || v.option1 || '',
        available: v.available,
        price: v.price,
        eu: null,
        uk: null,
        us: null,
        uk_men: null,
        uk_women: null,
        us_men: null,
        us_women: null
      };

      var name = parsed.title;

      // Try each pattern (most specific first)
      for (var i = 0; i < CONFIG.patterns.length; i++) {
        var p = CONFIG.patterns[i];
        var match = name.match(p.regex);
        if (match) {
          parsed.eu = match[p.euGroup].trim();
          if (p.secondSystem === 'uk') {
            parsed.uk = match[p.secondGroup];
          } else if (p.secondSystem === 'us') {
            parsed.us = match[p.secondGroup];
          }
          // secondSystem === null means EU-only, which is fine
          break;
        }
      }

      return parsed;
    });
  }

  function detectProductType(parsedVariants) {
    if (parsedVariants.length === 0) return { isShoe: false, isClothing: false };

    // Check if any variant has an EU size parsed
    var hasEU = parsedVariants.some(function (v) { return v.eu !== null; });
    if (hasEU) return { isShoe: true, isClothing: false };

    // Check if it's clothing (M, L, XL etc.)
    var isClothing = parsedVariants.every(function (v) {
      var title = v.title.trim().toUpperCase();
      return CONFIG.clothingSizes.indexOf(title) !== -1;
    });

    if (isClothing) return { isShoe: false, isClothing: true };

    // Unknown format
    return { isShoe: false, isClothing: false };
  }

  /* =============================================
     DATABASE LOOKUP
     ============================================= */

  function findBrandData(vendor, database) {
    if (!database || !database.brands || !vendor) return null;
    var vendorLower = vendor.toLowerCase().trim();
    var brands = database.brands;
    for (var key in brands) {
      if (brands.hasOwnProperty(key)) {
        if (key.toLowerCase().trim() === vendorLower) {
          return brands[key];
        }
      }
    }
    return null;
  }

  function enrichVariantsFromDatabase(parsedVariants, brandData) {
    if (!brandData || !brandData.sizes) return;
    var sizes = brandData.sizes;

    parsedVariants.forEach(function (v) {
      if (!v.eu) return;
      var sizeData = sizes[v.eu];
      if (!sizeData) return;

      // Enrich with database data
      if (sizeData.uk_men) v.uk_men = sizeData.uk_men;
      if (sizeData.uk_women) v.uk_women = sizeData.uk_women;
      if (sizeData.us_men) v.us_men = sizeData.us_men;
      if (sizeData.us_women) v.us_women = sizeData.us_women;

      // If variant had UK from name but no gender split, use database
      if (v.uk && !v.uk_men && sizeData.uk_men) v.uk_men = sizeData.uk_men;
      if (v.uk && !v.uk_women && sizeData.uk_women) v.uk_women = sizeData.uk_women;

      // If variant had US from name but no gender split, use database
      if (v.us && !v.us_men && sizeData.us_men) v.us_men = sizeData.us_men;
      if (v.us && !v.us_women && sizeData.us_women) v.us_women = sizeData.us_women;
    });
  }

  function detectAvailableTabs(parsedVariants) {
    var tabs = ['eu'];
    var hasUK = parsedVariants.some(function (v) {
      return v.uk || v.uk_men || v.uk_women;
    });
    var hasUS = parsedVariants.some(function (v) {
      return v.us || v.us_men || v.us_women;
    });
    if (hasUK) tabs.push('uk');
    if (hasUS) tabs.push('us');
    return tabs;
  }

  /**
   * Check if we actually have EU sizes to render as pills.
   * If not, we should NOT hide the original dropdown.
   */
  function hasPillsToRender() {
    return state.parsedVariants.some(function (v) { return v.eu !== null; });
  }

  /* =============================================
     RENDERING
     ============================================= */

  function render(container) {
    var html = '';

    // Header with title and tabs
    html += '<div class="size-selector__header">';
    html += '<span class="size-selector__title">' + t('select_size') + '</span>';
    html += renderTabs();
    html += '</div>';

    // Content area
    html += '<div class="size-selector__content">';
    html += renderContent();
    html += '</div>';

    // Status line
    html += renderStatus();

    container.innerHTML = html;
    bindEvents(container);
  }

  function renderTabs() {
    var orderedTabs = state.tabOrder.filter(function (tab) {
      return state.availableTabs.indexOf(tab) !== -1;
    });

    var html = '<div class="size-selector__tabs">';
    orderedTabs.forEach(function (tab) {
      var activeClass = state.tab === tab ? ' is-active' : '';
      html += '<button class="size-selector__tab' + activeClass + '" data-tab="' + tab + '">';
      html += t('tab_' + tab);
      html += '</button>';
    });
    html += '</div>';
    return html;
  }

  function renderContent() {
    if (state.isClothing) {
      return renderClothingPills();
    }

    if (state.tab === 'eu') {
      return renderEUPills();
    }

    // UK or US tab
    return renderGenderContent(state.tab);
  }

  function renderClothingPills() {
    var html = '<div class="size-selector__pills">';
    state.parsedVariants.forEach(function (v) {
      var label = v.title;
      var activeClass = state.selectedEU === v.title ? ' is-active' : '';
      var soldOutClass = !v.available ? ' is-sold-out' : '';
      var disabled = !v.available ? ' disabled' : '';
      html += '<button class="size-selector__pill' + activeClass + soldOutClass + '"' +
        ' data-eu="' + escapeAttr(v.title) + '"' + disabled + '>' + escapeHtml(label) + '</button>';
    });
    html += '</div>';
    return html;
  }

  function renderEUPills() {
    var html = '<div class="size-selector__pills">';
    state.parsedVariants.forEach(function (v) {
      if (!v.eu) return;
      var activeClass = state.selectedEU === v.eu ? ' is-active' : '';
      var soldOutClass = !v.available ? ' is-sold-out' : '';
      var disabled = !v.available ? ' disabled' : '';
      html += '<button class="size-selector__pill' + activeClass + soldOutClass + '"' +
        ' data-eu="' + escapeAttr(v.eu) + '"' + disabled + '>' + escapeHtml(v.eu) + '</button>';
    });
    html += '</div>';
    return html;
  }

  function renderGenderContent(system) {
    var menKey = system + '_men';
    var womenKey = system + '_women';

    var hasMen = state.parsedVariants.some(function (v) { return v[menKey]; });
    var hasWomen = state.parsedVariants.some(function (v) { return v[womenKey]; });
    var hasGeneric = state.parsedVariants.some(function (v) { return v[system]; });

    // No gender-specific data but generic values exist (brand not in database)
    // → show pills directly without gender selector
    if (!hasMen && !hasWomen && hasGeneric) {
      return renderSizePills(system, null);
    }

    // Only one gender available → show directly
    if (hasMen && !hasWomen) {
      return renderSizePills(menKey, 'men');
    }
    if (hasWomen && !hasMen) {
      return renderSizePills(womenKey, 'women');
    }

    // Both genders → show gender selector
    var html = '<div class="size-selector__gender">';
    html += '<button class="size-selector__gender-btn size-selector__gender-btn--men' +
      (state.gender === 'men' ? ' is-active' : '') + '" data-gender="men">';
    html += '<span class="size-selector__gender-icon">♂</span> ' + t('men');
    html += '</button>';
    html += '<button class="size-selector__gender-btn size-selector__gender-btn--women' +
      (state.gender === 'women' ? ' is-active' : '') + '" data-gender="women">';
    html += '<span class="size-selector__gender-icon">♀</span> ' + t('women');
    html += '</button>';
    html += '</div>';

    if (!state.gender) {
      html += '<div class="size-selector__gender-prompt">';
      html += '<span class="size-selector__gender-prompt-arrow">↑</span>';
      html += t('select_gender_first');
      html += '</div>';
    } else {
      var key = system + '_' + state.gender;
      html += renderSizePills(key, state.gender);
    }

    return html;
  }

  function renderSizePills(sizeKey, genderTint) {
    var tintClass = '';
    if (genderTint === 'men') tintClass = ' size-selector__pills--men';
    else if (genderTint === 'women') tintClass = ' size-selector__pills--women';
    var html = '<div class="size-selector__pills' + tintClass + '">';

    state.parsedVariants.forEach(function (v) {
      var sizeValue = v[sizeKey];
      if (!sizeValue) return; // Option A: hide sizes without value for this gender

      var activeClass = state.selectedEU === v.eu ? ' is-active' : '';
      var soldOutClass = !v.available ? ' is-sold-out' : '';
      var disabled = !v.available ? ' disabled' : '';
      html += '<button class="size-selector__pill' + activeClass + soldOutClass + '"' +
        ' data-eu="' + escapeAttr(v.eu) + '"' + disabled + '>' + escapeHtml(sizeValue) + '</button>';
    });

    html += '</div>';
    return html;
  }

  function renderStatus() {
    var html = '<div class="size-selector__status">';

    if (!state.selectedEU) {
      html += '<span style="opacity:0.4">' + (state.isClothing ? '👕' : '👟') + '</span> ';
      html += t('no_size_selected');
      html += '</div>';
      return html;
    }

    var v = state.parsedVariants.find(function (p) { return p.eu === state.selectedEU; });
    if (!v) {
      html += '</div>';
      return html;
    }

    if (state.isClothing) {
      html += '<span class="size-selector__status-label">' + escapeHtml(v.title) + '</span>';
      html += '</div>';
      return html;
    }

    var parts = [];

    // EU
    var euBold = state.tab === 'eu';
    parts.push(euBold
      ? '<span class="size-selector__status-label">EU ' + escapeHtml(v.eu) + '</span>'
      : 'EU ' + escapeHtml(v.eu));

    // UK – if men and women sizes differ, show both with gender icons
    if (v.uk_men && v.uk_women && v.uk_men !== v.uk_women) {
      var ukMenBold = state.tab === 'uk' && state.gender === 'men';
      parts.push(ukMenBold
        ? '<span class="size-selector__status-label">UK ♂ ' + escapeHtml(v.uk_men) + '</span>'
        : 'UK ♂ ' + escapeHtml(v.uk_men));
      var ukWomenBold = state.tab === 'uk' && state.gender === 'women';
      parts.push(ukWomenBold
        ? '<span class="size-selector__status-label">UK ♀ ' + escapeHtml(v.uk_women) + '</span>'
        : 'UK ♀ ' + escapeHtml(v.uk_women));
    } else {
      // Same UK size for both genders, or only one gender, or generic
      var ukVal = v.uk_men || v.uk_women || v.uk;
      if (ukVal) {
        var ukBold = state.tab === 'uk';
        parts.push(ukBold
          ? '<span class="size-selector__status-label">UK ' + escapeHtml(ukVal) + '</span>'
          : 'UK ' + escapeHtml(ukVal));
      }
    }

    // US Men
    if (v.us_men) {
      var usMenBold = state.tab === 'us' && state.gender === 'men';
      parts.push(usMenBold
        ? '<span class="size-selector__status-label">US ♂ ' + escapeHtml(v.us_men) + '</span>'
        : 'US ♂ ' + escapeHtml(v.us_men));
    }

    // US Women
    if (v.us_women) {
      var usWomenBold = state.tab === 'us' && state.gender === 'women';
      parts.push(usWomenBold
        ? '<span class="size-selector__status-label">US ♀ ' + escapeHtml(v.us_women) + '</span>'
        : 'US ♀ ' + escapeHtml(v.us_women));
    }

    // If only generic US (no men/women split)
    if (v.us && !v.us_men && !v.us_women) {
      var usBold = state.tab === 'us';
      parts.push(usBold
        ? '<span class="size-selector__status-label">US ' + escapeHtml(v.us) + '</span>'
        : 'US ' + escapeHtml(v.us));
    }

    html += parts.join(' | ');
    html += '</div>';
    return html;
  }

  /* =============================================
     EVENTS
     ============================================= */

  function bindEvents(container) {
    // Tab clicks
    container.querySelectorAll('[data-tab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.tab = btn.dataset.tab;
        state.gender = null;
        updateLineItemProperty(); /* BS24-Fix F1 (22.08.2026): Property auch beim Tab-Wechsel aktualisieren */
        render(container);
      });
    });

    // Gender clicks
    container.querySelectorAll('[data-gender]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.gender = btn.dataset.gender;
        updateLineItemProperty(); /* BS24-Fix F1 (22.08.2026): Property auch beim Geschlechts-Wechsel aktualisieren */
        render(container);
      });
    });

    // Pill clicks
    container.querySelectorAll('.size-selector__pill:not(.is-sold-out)').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var eu = btn.dataset.eu;
        state.selectedEU = eu;
        selectShopifyVariant(eu);
        updateLineItemProperty();
        render(container);
      });
    });
  }

  /* =============================================
     SHOPIFY INTEGRATION
     ============================================= */

  function selectShopifyVariant(euSize) {
    // Find the variant with this EU size
    var variant = state.parsedVariants.find(function (v) { return v.eu === euSize || v.title === euSize; });
    if (!variant) return;

    var variantId = variant.id;

    // Method 1: Update the hidden variant ID input in the product form
    var forms = document.querySelectorAll('form[action="/cart/add"], form[data-type="add-to-cart-form"]');
    forms.forEach(function (form) {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
        idInput.disabled = !variant.available;
      }
    });

    // Method 2: Update the original select dropdown and trigger change
    var selects = document.querySelectorAll('variant-selects select, .product-form__input--dropdown select');
    selects.forEach(function (select) {
      for (var i = 0; i < select.options.length; i++) {
        var optText = select.options[i].text || select.options[i].textContent || '';
        // Match by EU size in the option text, or exact match for clothing
        if (optText.indexOf('EU ' + euSize) !== -1 || optText.trim() === euSize) {
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    });

    // Method 3: Try to trigger Berlin theme's variant change handler
    var variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      if (typeof variantSelects.onVariantChange === 'function') {
        variantSelects.onVariantChange();
      } else if (typeof variantSelects.updateOptions === 'function') {
        variantSelects.updateOptions();
      }
    }

    // Update the submit button state
    var submitBtns = document.querySelectorAll('[id^="ProductSubmitButton"], .product-form__submit');
    submitBtns.forEach(function (btn) {
      btn.disabled = !variant.available;
    });

    // Update URL with variant parameter
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  function updateLineItemProperty() {
    // Find ALL cart forms (some themes have multiple)
    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"], form[action="/cart/add"]');
    if (forms.length === 0) return;

    var propertyName = 'properties[' + t('selected_size') + ']';

    forms.forEach(function (form) {
      // Remove existing line item property input
      var existing = form.querySelector('input[name="' + propertyName + '"]');
      if (existing) existing.remove();

      // Only add for UK/US tabs
      if (state.tab === 'eu') return;
      if (!state.selectedEU) return;

      var v = state.parsedVariants.find(function (p) { return p.eu === state.selectedEU; });
      if (!v) return;

      var label = '';
      if (state.tab === 'uk') {
        if (state.gender === 'men' && v.uk_men) {
          label = t('uk_men') + ' ' + v.uk_men;
        } else if (state.gender === 'women' && v.uk_women) {
          label = t('uk_women') + ' ' + v.uk_women;
        } else if (v.uk) {
          label = 'UK ' + v.uk;
        }
      } else if (state.tab === 'us') {
        if (state.gender === 'men' && v.us_men) {
          label = t('us_men') + ' ' + v.us_men;
        } else if (state.gender === 'women' && v.us_women) {
          label = t('us_women') + ' ' + v.us_women;
        } else if (v.us) {
          label = 'US ' + v.us;
        }
      }

      if (!label) return;

      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = propertyName;
      input.value = label;
      form.appendChild(input);
    });
  }

  function addSizeSelectorActiveClass() {
    // Add class to hide original dropdown
    var wrapper = document.querySelector('.product-parameters__variant');
    if (wrapper) {
      wrapper.classList.add('size-selector-active');
    }
  }

  /* =============================================
     HELPERS
     ============================================= */

  function t(key) {
    return state.translations[key] || key;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* =============================================
     BOOT
     ============================================= */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();